import { AIRCAT35_SYSTEMS } from "@/data/aircatPmsComponentTree";

// Legacy categories kept for backward compatibility
export const EQUIPMENT_CATEGORIES = [
  { value: "engine", label: "Engine Room", icon: "⚙️" },
  { value: "electrical", label: "Electrical", icon: "⚡" },
  { value: "deck", label: "Deck Equipment", icon: "⚓" },
  { value: "navigation", label: "Navigation", icon: "🧭" },
  { value: "safety", label: "Safety / LSA / FFA", icon: "🛟" },
  { value: "hull", label: "Hull & Structure", icon: "🚢" },
  { value: "hvac", label: "HVAC", icon: "❄️" },
  { value: "auxiliary", label: "Auxiliary Systems", icon: "🔧" },
  { value: "propulsion", label: "Propulsion", icon: "🔄" },
  { value: "fuel", label: "Fuel System", icon: "⛽" },
];

// 19-system hierarchy from AIRCAT 35 Operations Manual
export const SYSTEM_CATEGORIES = AIRCAT35_SYSTEMS.map(s => ({
  value: s.id,
  label: s.name,
  icon: s.icon,
  category: s.category,
}));

export const CRITICALITY = [
  { value: "A", label: "A — Critical (Class-Essential)", color: "bg-red-100 text-red-700" },
  { value: "B", label: "B — Essential", color: "bg-amber-100 text-amber-700" },
  { value: "C", label: "C — Standard", color: "bg-blue-100 text-blue-700" },
];

export const JOB_TYPES = [
  { value: "calendar", label: "Calendar-Based (TBM)" },
  { value: "running_hours", label: "Running Hours" },
  { value: "condition_based", label: "Condition-Based (CBM)" },
];

export const DEPARTMENTS = [
  { value: "engine", label: "Engine" },
  { value: "deck", label: "Deck" },
  { value: "electrical", label: "Electrical" },
  { value: "navigation", label: "Navigation" },
  { value: "safety", label: "Safety" },
];

export const JOB_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  scheduled: { label: "Scheduled", color: "bg-blue-100 text-blue-700" },
  in_progress: { label: "In Progress", color: "bg-amber-100 text-amber-700" },
  overdue: { label: "Overdue", color: "bg-red-100 text-red-700" },
  completed: { label: "Completed", color: "bg-emerald-100 text-emerald-700" },
  deferred: { label: "Deferred", color: "bg-purple-100 text-purple-700" },
  cancelled: { label: "Cancelled", color: "bg-muted text-muted-foreground" },
};

export const WO_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  open: { label: "Open", color: "bg-blue-100 text-blue-700" },
  in_progress: { label: "In Progress", color: "bg-amber-100 text-amber-700" },
  awaiting_parts: { label: "Awaiting Parts", color: "bg-orange-100 text-orange-700" },
  completed: { label: "Completed", color: "bg-emerald-100 text-emerald-700" },
  closed: { label: "Closed", color: "bg-muted text-muted-foreground" },
  cancelled: { label: "Cancelled", color: "bg-muted text-muted-foreground" },
};

export const PRIORITY_CONFIG: Record<string, { label: string; color: string }> = {
  critical: { label: "Critical", color: "bg-red-100 text-red-700" },
  high: { label: "High", color: "bg-orange-100 text-orange-700" },
  medium: { label: "Medium", color: "bg-amber-100 text-amber-700" },
  low: { label: "Low", color: "bg-muted text-muted-foreground" },
};

export const DEFERRAL_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  pending: { label: "Pending", color: "bg-amber-100 text-amber-700" },
  ce_approved: { label: "CE Approved", color: "bg-blue-100 text-blue-700" },
  approved: { label: "Approved", color: "bg-emerald-100 text-emerald-700" },
  rejected: { label: "Rejected", color: "bg-red-100 text-red-700" },
};

export const DEFECT_SEVERITY_CONFIG: Record<string, { label: string; color: string }> = {
  critical: { label: "Critical", color: "bg-red-100 text-red-700" },
  major: { label: "Major", color: "bg-orange-100 text-orange-700" },
  minor: { label: "Minor", color: "bg-amber-100 text-amber-700" },
  observation: { label: "Observation", color: "bg-blue-100 text-blue-700" },
};

export const DEFECT_STATUS_CONFIG: Record<string, { label: string; color: string }> = {
  open: { label: "Open", color: "bg-red-100 text-red-700" },
  in_progress: { label: "In Progress", color: "bg-amber-100 text-amber-700" },
  closed: { label: "Closed", color: "bg-emerald-100 text-emerald-700" },
  deferred: { label: "Deferred", color: "bg-purple-100 text-purple-700" },
};

export const DEFECT_TYPES = [
  { value: "defect", label: "Defect" },
  { value: "observation", label: "Observation" },
  { value: "near_miss", label: "Near Miss" },
  { value: "non_conformity", label: "Non-Conformity" },
];

export const SPARE_CATEGORIES = [
  "engine", "electrical", "deck", "safety", "navigation", "hull", "consumable", "general", "propulsion", "fuel",
];

export const PMS_ROLES = [
  { value: "crew", label: "Crew" },
  { value: "engineer", label: "Engineer" },
  { value: "chief_engineer", label: "Chief Engineer" },
  { value: "technical_manager", label: "Technical Manager" },
];

export const CONDITION_PARAMETER_TYPES = [
  { value: "vibration", label: "Vibration Analysis", unit: "mm/s" },
  { value: "temperature", label: "Temperature", unit: "°C" },
  { value: "oil_analysis", label: "Oil Analysis (TBN)", unit: "mgKOH/g" },
  { value: "oil_viscosity", label: "Oil Viscosity", unit: "cSt" },
  { value: "oil_water", label: "Oil Water Content", unit: "ppm" },
  { value: "oil_metal", label: "Oil Metal Particles", unit: "ppm" },
  { value: "pressure", label: "Pressure", unit: "bar" },
  { value: "differential_pressure", label: "Differential Pressure", unit: "bar" },
  { value: "insulation_resistance", label: "Insulation Resistance", unit: "MΩ" },
  { value: "current", label: "Current Draw", unit: "A" },
  { value: "flow_rate", label: "Flow Rate", unit: "m³/h" },
  { value: "thickness", label: "Ultrasonic Thickness", unit: "mm" },
  { value: "rpm", label: "RPM", unit: "rpm" },
  { value: "noise", label: "Noise Level", unit: "dB" },
  { value: "exhaust_temp", label: "Exhaust Gas Temperature", unit: "°C" },
  { value: "fuel_consumption", label: "Fuel Consumption", unit: "l/h" },
];

export const CLASS_SOCIETIES = [
  "ABS", "DNV", "Lloyd's Register (LR)", "Bureau Veritas (BV)", "ClassNK", "RINA", "Korean Register (KR)",
];

export const TECHNICAL_MANAGER_ROLES = ["Manager", "Shoreside Leadership", "Executive Team"];
export const CE_ROLES = ["Engineer", "Manager", "Shoreside Leadership", "Executive Team"];

// Visit Report categories based on industry vessel inspection standards
export const VISI_CATEGORIES = [
  { id: "cert", label: "Certificates & Documentation", items: [
    "Safety Management Certificate (SMC)", "Document of Compliance (DOC)", "International Ship Security Certificate (ISSC)",
    "Cargo Ship Safety Equipment Certificate", "Load Line Certificate", "International Tonnage Certificate",
    "IOPP Certificate", "Class Certificate", "Certificate of Registry", "Minimum Safe Manning Document",
    "Continuous Synopsis Record (CSR)", "Ship Sanitation Control Certificate",
  ]},
  { id: "nav", label: "Navigation & Bridge", items: [
    "ECDIS operational & updated", "Radar / ARPA functionality", "AIS operational", "Magnetic & Gyro compass",
    "Navigation lights & shapes", "Bridge team management procedures", "Passage plan in place",
    "Publications & charts updated", "VDR operational & tested", "Sound signals equipment",
  ]},
  { id: "lsa", label: "Life-Saving Appliances", items: [
    "Lifeboats — condition & davits", "Liferafts — servicing & HRU", "Lifejackets — condition & stowage",
    "Immersion suits — availability & condition", "MOB equipment & procedures", "EPIRB — registration & test",
    "SART — test & battery", "Pyrotechnics — expiry dates", "Line-throwing apparatus",
  ]},
  { id: "ffa", label: "Fire Fighting Appliances", items: [
    "Fixed fire-fighting systems (CO2/Foam/Water Mist)", "Fire pumps — operational test", "Fire hoses & nozzles — condition",
    "Portable extinguishers — serviced & inspected", "Fire detection / alarm system", "Emergency fire pump",
    "Fireman's outfits — complete sets", "Fire doors — operation & self-closing", "Fire dampers inspected",
  ]},
  { id: "hull", label: "Hull & Deck", items: [
    "Hull condition — coating & corrosion", "Anchor & chain — condition", "Mooring equipment — condition & maintenance",
    "Watertight doors & openings", "Freeing ports operational", "Deck machinery condition",
    "Crane / lifting equipment — certified", "Hatch covers — watertight integrity",
  ]},
  { id: "engine", label: "Engine Room", items: [
    "Main engine condition & alarms", "Generators — operational & alarms", "Emergency generator — start & load test",
    "Steering gear — main & auxiliary test", "Bilge / ballast system operational", "Oily water separator — operational & 15ppm alarm",
    "Fuel oil system — leak-free", "Ventilation fans operational", "Engine room cleanliness & housekeeping",
    "Emergency procedures posted",
  ]},
  { id: "pollution", label: "Pollution Prevention", items: [
    "Oil Record Book Part I & II completed", "Garbage Management Plan & record book", "Ballast Water Management Plan",
    "Sewage treatment plant operational", "NOx Technical File available", "Bunker Delivery Notes retained",
    "Ship Energy Efficiency Management Plan (SEEMP)", "Incinerator logbook maintained",
  ]},
  { id: "crew", label: "Crew & Personnel", items: [
    "Crew certificates valid (STCW)", "Medical certificates valid", "Rest hour records compliant (MLC 2006)",
    "Drug & alcohol policy in place", "Crew familiarisation records", "Safety committee minutes",
    "Master review of SMS", "Training records up to date",
  ]},
  { id: "security", label: "Security (ISPS)", items: [
    "Ship Security Plan available", "SSO appointed & trained", "Security drills conducted",
    "Access control procedures", "Restricted areas identified", "Declaration of Security (DoS) records",
  ]},
  { id: "ism", label: "ISM / SMS", items: [
    "SMS manual available & current", "Master's responsibility & authority documented", "Emergency preparedness procedures",
    "Internal audit programme", "Non-conformity & corrective action system", "Management review conducted",
    "Planned maintenance system functioning", "Document control procedures",
  ]},
];

// Helpers
export const StatusBadge = ({ status, config }: { status: string; config: Record<string, { label: string; color: string }> }) => {
  const s = config[status] || { label: status, color: "bg-muted text-muted-foreground" };
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${s.color}`}>{s.label}</span>;
};

export const PriorityBadge = ({ priority }: { priority: string }) => {
  const p = PRIORITY_CONFIG[priority] || PRIORITY_CONFIG.medium;
  return <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold uppercase ${p.color}`}>{p.label}</span>;
};
